import axios from 'axios';
// Simple JWT decoder
const decodeJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''));
        return JSON.parse(jsonPayload);
    }
    catch (e) {
        console.error('Error decoding JWT:', e);
        return null;
    }
};
// Environment configuration
const getEnv = (key, fallback = '') => {
    // Prefer Vite env when available (browser builds)
    const meta = (typeof import.meta !== 'undefined') ? import.meta : undefined;
    if (meta?.env) {
        const viteKey = `VITE_${key}`;
        const value = meta.env[viteKey];
        if (value !== undefined) {
            console.log(`[DEBUG] Found ${viteKey} in Vite env:`, value);
            return value;
        }
    }
    // Fallback to Node env when available (server builds)
    const g = (typeof globalThis !== 'undefined') ? globalThis : undefined;
    const penv = g?.process?.env;
    if (penv) {
        const value = penv[key] || penv[`VITE_${key}`];
        if (value) {
            console.log(`[DEBUG] Found ${key} in process.env:`, value);
            return value;
        }
    }
    console.log(`[DEBUG] Using fallback for ${key}:`, fallback);
    return fallback;
};
// API Configuration
export const XOXO_API_URL = getEnv('XOXO_API_BASE_URL', 'https://dev-api.xoxobeverages.xyz/v1');
const LOCAL_KIOSK_API_URL = 'http://localhost:8080/api';
function joinUrl(base, path) {
    const b = (base ?? '').toString().trim();
    const p = (path ?? '').toString().trim();
    if (!b)
        return p;
    const baseNoSlash = b.endsWith('/') ? b.slice(0, -1) : b;
    const pathWithSlash = p.startsWith('/') ? p : `/${p}`;
    return `${baseNoSlash}${pathWithSlash}`;
}
// Determine kiosk API URL at runtime
// In production (deployed), use relative /api path (proxied via CloudFront)
// In development (localhost:3000), use full URL to kiosk-server on port 8080
function getKioskApiUrl() {
    // Check for explicit env override first
    const envUrl = getEnv('KIOSK_API_URL');
    if (envUrl)
        return envUrl;
    // In browser, check if we're on localhost (dev) or deployed (prod)
    if (typeof window !== 'undefined') {
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isLocalhost) {
            return LOCAL_KIOSK_API_URL;
        }
        // Production - use relative path
        return '/api';
    }
    // Server-side or build time - default to local
    return LOCAL_KIOSK_API_URL;
}
export const KIOSK_API_URL = getKioskApiUrl();
export const MCP_STREAMABLE_HTTP_URL = joinUrl(KIOSK_API_URL, '/mcp/stream');
export function getMcpStreamableHttpUrl(baseUrl = KIOSK_API_URL) {
    return joinUrl(baseUrl, '/mcp/stream');
}
// Get API key from environment variables
export const API_KEY = getEnv('XOXO_API_KEY') || getEnv('API_KEY');
// Validate required environment variables
if (!API_KEY) {
    console.error('API_KEY is not set. Please check your environment variables.');
}
// Log API configuration (without sensitive data)
console.log('[DEBUG] XOXO API URL:', XOXO_API_URL);
console.log('[DEBUG] Kiosk API URL:', KIOSK_API_URL);
console.log('[DEBUG] API Key:', API_KEY ? '*** (set)' : 'Not set');
// Server-safe storage for tokens
const tokenStorage = {
    _storage: new Map(),
    getItem(key) {
        if (typeof window !== 'undefined') {
            // Browser environment
            return localStorage.getItem(key);
        }
        // Node.js environment
        return this._storage.get(key) || null;
    },
    setItem(key, value) {
        if (typeof window !== 'undefined') {
            // Browser environment
            localStorage.setItem(key, value);
        }
        else {
            // Node.js environment
            this._storage.set(key, value);
        }
    },
    removeItem(key) {
        if (typeof window !== 'undefined') {
            // Browser environment
            localStorage.removeItem(key);
        }
        else {
            // Node.js environment
            this._storage.delete(key);
        }
    }
};
// Token management
export const getToken = () => {
    const token = tokenStorage.getItem('refreshToken');
    if (!token) {
        console.warn('No refresh token found in storage');
    }
    else {
        const decoded = decodeJwt(token);
        console.log('Using refresh token with details:', {
            type: decoded?.type,
            exp: decoded?.exp ? new Date(decoded.exp * 1000).toISOString() : 'N/A',
            isExpired: decoded?.exp ? Date.now() > decoded.exp * 1000 : 'unknown'
        });
    }
    return token;
};
export const setToken = (token) => {
    console.log('Setting refresh token in storage');
    tokenStorage.setItem('refreshToken', token);
};
export const removeToken = () => {
    console.log('Removing tokens from storage');
    tokenStorage.removeItem('refreshToken');
    tokenStorage.removeItem('accessToken');
    tokenStorage.removeItem('user');
};
// Create API instances with their respective base URLs
export const xoxoApi = createAxiosInstance(XOXO_API_URL);
export const kioskApi = createAxiosInstance(KIOSK_API_URL);
// Log API instance creation
console.log('[DEBUG] Created API instances:');
console.log('- xoxoApi:', xoxoApi.defaults.baseURL);
console.log('- kioskApi:', kioskApi.defaults.baseURL);
// Set up request interceptors for XOXO API
xoxoApi.interceptors.request.use((config) => {
    console.log('[DEBUG] XOXO API Request:', config.url);
    const token = getToken();
    // Add API key header for XOXO API
    config.headers['x-api-key'] = API_KEY;
    // Add auth token if available
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Log the request details (excluding sensitive data)
    const logData = {
        method: config.method?.toUpperCase(),
        url: config.url,
        headers: {
            ...config.headers,
            // Redact sensitive information in logs
            Authorization: config.headers.Authorization ? 'Bearer [TOKEN]' : undefined,
            'x-api-key': '[REDACTED]'
        },
        hasToken: !!token
    };
    // Only include params if they exist
    if (Object.keys(config.params || {}).length > 0) {
        logData.params = config.params;
    }
    // Only include data if it exists and is not FormData
    if (config.data && !(config.data instanceof FormData)) {
        logData.data = config.data;
    }
    else if (config.data) {
        logData.data = '[FormData]';
    }
    console.log(`[XOXO API Request]`, logData);
    return config;
});
// Set up response interceptors for XOXO API
xoxoApi.interceptors.response.use((response) => {
    console.log('[DEBUG] XOXO API Response:', response.config.url, response.status);
    return response;
}, (error) => {
    console.error('[XOXO API] Response Error:', error);
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
        console.log('[Auth] XOXO API token expired or invalid, removing token');
        removeToken();
    }
    return Promise.reject(error);
});
// Set up request interceptors for Kiosk API
kioskApi.interceptors.request.use((config) => {
    console.log('[DEBUG] Kiosk API Request:', config.url);
    const token = getToken();
    // Add auth token if available
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});
// Set up response interceptors for Kiosk API
kioskApi.interceptors.response.use((response) => {
    console.log('[DEBUG] Kiosk API Response:', response.config.url, response.status);
    return response;
}, (error) => {
    console.error('[Kiosk API] Response Error:', error);
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
        console.log('[Auth] Kiosk API token expired or invalid, removing token');
        removeToken();
    }
    return Promise.reject(error);
});
// Helper function to create an Axios instance with a given base URL
function createAxiosInstance(baseUrl) {
    return axios.create({
        baseURL: baseUrl,
        timeout: 120000, // 2 minutes timeout for long-running operations like MBTI generation
        headers: {
            'Content-Type': 'application/json',
        },
    });
}
